import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContentService, ServicePathProviderService } from '@blk/cwp-ng-framework';
import { ExcelGenerateConfig, WebUserDataConstants } from '@ddt-web/domain';
import * as dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import * as get from 'lodash/get';
import * as pako from 'pako';
import { Observable, ReplaySubject } from 'rxjs';
import { first, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { PageDimensions } from '../models/page-dimensions.enum';
import { ReportData } from '../models/report-data';
import { ReportError } from '../models/report-error';

declare let TextDecoder;
@Injectable({
	providedIn: 'root'
})
export class ReportGenerationService {
	private static readonly ERROR_MARKER_CODES = new Uint8Array([69, 82, 82, 79, 82, 33]);
	private static readonly EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

	private static readonly DECODE_ISO88592: string =
		// eslint-disable-next-line max-len
		'\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~ Ą˘Ł¤ĽŚ§¨ŠŞŤŹ­ŽŻ°ą˛ł´ľśˇ¸šşťź˝žżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢßŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙';

	// Flag in url to disable pdf compression
	public disablePdfCompression = window.location.href.includes('disablePdfGzip');

	constructor(
		private servicePathProvider: ServicePathProviderService,
		private http: HttpClient,
		private contentService: ContentService
	) { }

	public getExcel(config: ExcelGenerateConfig, data: any, reportName?: string): Observable<ArrayBuffer> {
		reportName = reportName || this.getDefaultReportExcelFileName();
		const reportUrl = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.EXCEL_GENERATE + config;
		const requestBody = { data };

		return this.http.post(reportUrl, requestBody, {
			responseType: 'arraybuffer'
		}).pipe(
			tap(
				data => {
					this.saveExcelFile(reportName, data);
				},
				err => {
					let errorJson;
					try {
						// this way also works in IE
						const decodedError = String.fromCharCode.apply(null, new Uint8Array(err.error));
						errorJson = JSON.parse(decodedError);
					} catch {
						// set errorJson to generic EXCEL error if decoding/ json parsing fails
						errorJson = { errorCode: 'EXCEL_GENERATION_ERROR' };
					}

					// if error structure changes, update ExcelGenerateErrorHandlerService
					const error = get(errorJson, 'errorCode') ? errorJson : { errorCode: 'EXCEL_GENERATION_ERROR' };
					throw new Error(JSON.stringify(error));
				}
			));
	}

	public generateReport(
		reportData: ReportData,
		isLandscape?: boolean,
		exportAsPNG?: boolean
	): Observable<boolean> {
		return this.savePdfReport(this.getPdf(reportData.svgPages, reportData.save, this.disablePdfCompression, isLandscape, exportAsPNG), reportData, exportAsPNG);
	}

	public ajaxGenerateReport(reportData: ReportData): Observable<any> {
		const requestBody: Object = { svgPages: reportData.svgPages, save: reportData.save };
		if (reportData.compressPdf && !this.disablePdfCompression) {
			Object.assign(requestBody, this.addCompressionForPdfApiGeneration(reportData.svgPages));
		}

		const httpOptions: Object = { responseType: 'blob' };
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.PDF_SVG_API;

		return this.http.post(url, requestBody, httpOptions).pipe(
			mergeMap(response => {
				return new Observable(obs => {
					const reader = new FileReader();
					reader.onabort = err => obs.error(err);
					reader.onerror = err => obs.error(err);
					reader.onloadend = () => obs.complete();
					reader.onload = () => obs.next(reader.result);
					return reader.readAsArrayBuffer(<Blob>response);
				});
			}),
			map((data: ArrayBuffer) => {
				let binary = '';
				const bytes = new Uint8Array(data);
				const len = bytes.byteLength;
				for (let i = 0; i < len; i++) {
					binary += String.fromCharCode(bytes[i]);
				}
				return window.btoa(binary);
			})
		);
	}

	public getSavedApbPdf(getPdfUrl: string, fileName: string): Observable<any> {
		const reportData = new ReportData();
		reportData.fileName = fileName;

		return this.savePdfReport(this.http.get(getPdfUrl, { responseType: 'arraybuffer' }), reportData);
	}

	public getPdf(svgPages: string[], save?: boolean, disablePdfGzip = false, isLandscape = false, exportAsPNG = false): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.PDF_SVG_API;

		const requestParams: Object = {
			svgPages: svgPages,
			save: save,
			compressedSvg: false,
			exportAsPNG: exportAsPNG,
			pageDimensions: isLandscape ? PageDimensions.US_LETTER_LANDSCAPE : PageDimensions.US_LETTER
		};

		if (!disablePdfGzip) {
			Object.assign(requestParams, this.addCompressionForPdfApiGeneration(svgPages));
		}

		return this.http.post(url, requestParams, { responseType: 'arraybuffer' });
	}

	private addCompressionForPdfApiGeneration(svgPages: string[]): Object {
		const data = JSON.stringify(svgPages);
		const result = pako.gzip(data);
		const stringOfCompressedSvgPages = window['TextDecoder'] ? new TextDecoder('iso-8859-2').decode(result) : this.decodeBytesToIso(result);

		return {
			svgPages: [stringOfCompressedSvgPages],
			compressedSvg: true
		};
	}

	private decodeBytesToIso(bytes: Uint8Array): string {
		let result = '';
		for (let i = 0; i < bytes.length; i++) {
			result += ReportGenerationService.DECODE_ISO88592.charAt(bytes[i]);
		}
		return result;
	}

	private bufferArrayEquals(buf1: Uint8Array, buf2: Uint8Array): boolean {
		if (buf1.byteLength !== buf2.byteLength) {
			return false;
		}
		for (let i = 0; i !== buf1.byteLength; i++) {
			if (buf1[i] !== buf2[i]) {
				return false;
			}
		}
		return true;
	}

	private saveExcelFile(fileName: string, data: any): void {
		const blob = new Blob([data], { type: ReportGenerationService.EXCEL_CONTENT_TYPE });
		FileSaver.saveAs(blob, fileName);
	}

	private getDefaultReportExcelFileName(): string {
		const date = dayjs().format('YYYYMMDD');
		const prefix = this.contentService.get('EXCEL_REPORT_FILE_NAME_PREFIX', 'report');

		return `${prefix}-${date}.xlsx`;
	}

	private savePdfReport(data: Observable<any>, reportData: ReportData, exportAsPNG?: boolean): Observable<boolean> {
		const result = new ReplaySubject<boolean>();
		return data.pipe(
			take(1),
			tap(
				data => {
					try {
						const blob = exportAsPNG ? new Blob([data], { type: 'application/zip' }) : new Blob([data], { type: 'application/pdf' });
						const isError = this.bufferArrayEquals(
							ReportGenerationService.ERROR_MARKER_CODES,
							new Uint8Array(data.slice(data.byteLength - ReportGenerationService.ERROR_MARKER_CODES.length))
						);
						if (isError) {
							// If the Error marker is detected user can't see the report and we need to abort the flow!
							const err = new ReportError('PDF generation failed on the server, error marker found in the response');
							result.error(err);
							throw err;
						}
						FileSaver.saveAs(blob, reportData.fileName);

						result.next(true);
					} catch (err) {
						result.error(err);
						console.error('Failed to read response', err);
					}
				},
				err => {
					result.error(err);
					console.error('Failed to read response', err);
				}),
			switchMap(() => result.asObservable().pipe(first()))
		);
	}
}
